import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Zap,
  Map,
  Bell,
  ShieldCheck,
  Activity,
  Receipt,
  HeadphonesIcon,
  Globe,
  Cpu,
  Ticket,
  Heart,
  Users,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { UserRole } from '../../types';
import AvionLogo from '../shared/AvionLogo';

interface NavItem {
  label: string;
  path:  string;
  icon:  React.ReactNode;
}

const nationalNav: NavItem[] = [
  { label: 'Overview',         path: '/national/overview',   icon: <LayoutDashboard size={15} /> },
  { label: 'Generation',       path: '/national/generation', icon: <Zap size={15} /> },
  { label: 'States & DISCOMs', path: '/national/states',    icon: <Map size={15} /> },
  { label: 'Alarms',           path: '/national/alarms',     icon: <Bell size={15} /> },
  { label: 'Compliance',       path: '/national/compliance', icon: <ShieldCheck size={15} /> },
];

const stateNav: NavItem[] = [
  { label: 'Overview',   path: '/state/overview',   icon: <LayoutDashboard size={15} /> },
  { label: 'Generation', path: '/state/generation', icon: <Zap size={15} /> },
  { label: 'Districts',  path: '/state/districts',  icon: <Map size={15} /> },
  { label: 'Alarms',     path: '/state/alarms',     icon: <Bell size={15} /> },
];

const discomNav: NavItem[] = [
  { label: 'Overview',        path: '/discom/overview',       icon: <LayoutDashboard size={15} /> },
  { label: 'Generation',      path: '/discom/generation',     icon: <Zap size={15} /> },
  { label: 'Grid Operations', path: '/discom/grid-ops',       icon: <Activity size={15} /> },
  { label: 'Net Metering',    path: '/discom/net-metering',   icon: <Receipt size={15} /> },
  { label: 'Circles',         path: '/discom/circles',        icon: <Globe size={15} /> },
  { label: 'Alarms',          path: '/discom/alarms',         icon: <Bell size={15} /> },
  { label: 'Remote Control',  path: '/discom/remote-control', icon: <Cpu size={15} /> },
];

const consumerNav: NavItem[] = [
  { label: 'Dashboard',       path: '/consumer/dashboard',  icon: <LayoutDashboard size={15} /> },
  { label: 'Electrical',      path: '/consumer/electrical', icon: <Zap size={15} /> },
  { label: 'Alarms',          path: '/consumer/alarms',     icon: <Bell size={15} /> },
  { label: 'Billing & Savings', path: '/consumer/billing',  icon: <Receipt size={15} /> },
  { label: 'Support',         path: '/consumer/support',    icon: <HeadphonesIcon size={15} /> },
];

const vendorNav: NavItem[] = [
  { label: 'Overview',      path: '/vendor/overview',   icon: <LayoutDashboard size={15} /> },
  { label: 'Generation',    path: '/vendor/generation', icon: <Zap size={15} /> },
  { label: 'Plant List',    path: '/vendor/plants',     icon: <Map size={15} /> },
  { label: 'Tickets',       path: '/vendor/tickets',    icon: <Ticket size={15} /> },
  { label: 'Device Health', path: '/vendor/devices',    icon: <Heart size={15} /> },
];

const navByRole: Record<UserRole, NavItem[]> = {
  [UserRole.NATIONAL]: nationalNav,
  [UserRole.STATE]:    stateNav,
  [UserRole.DISCOM]:   discomNav,
  [UserRole.CONSUMER]: consumerNav,
  [UserRole.VENDOR]:   vendorNav,
};

const sectionLabels: Record<UserRole, string> = {
  [UserRole.NATIONAL]: 'National',
  [UserRole.STATE]:    'Gujarat',
  [UserRole.DISCOM]:   'PGVCL',
  [UserRole.CONSUMER]: 'GJ-0482',
  [UserRole.VENDOR]:   'Zebron Solar',
};

export default function Sidebar() {
  const { currentRole, isAdmin } = useAuth();
  const { isDark } = useTheme();
  const items = navByRole[currentRole];
  const isConsumer = currentRole === UserRole.CONSUMER;

  const activeClass = isConsumer
    ? 'border-l-2 border-consumer-green bg-green-50 text-consumer-green font-semibold'
    : 'border-l-2 border-accent bg-bg-tertiary text-accent font-semibold';

  const inactiveClass =
    'border-l-2 border-transparent text-text-secondary hover:bg-bg-secondary hover:text-text-primary';

  return (
    <aside className="w-[200px] flex-shrink-0 bg-bg-primary border-r border-border flex flex-col h-full overflow-y-auto">

      {/* Section label */}
      <div className="px-3 pt-4 pb-2">
        <p className="text-xs font-semibold text-text-tertiary uppercase tracking-wider px-2">
          {sectionLabels[currentRole]}
        </p>
      </div>

      {/* Main nav */}
      <nav className="flex-1 px-2 pb-4 space-y-0.5">
        {items.map(item => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2.5 rounded-r-input text-xs transition-colors ${isActive ? activeClass : inactiveClass}`
            }
          >
            <span className="flex-shrink-0">{item.icon}</span>
            <span className="truncate">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* ── Admin section (admin only) ── */}
      {isAdmin && (
        <div className="border-t border-border px-2 py-2">
          <p className="text-xs font-semibold text-text-tertiary uppercase tracking-wider px-3 pb-1">
            Admin
          </p>
          <NavLink
            to="/admin/users"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2.5 rounded-r-input text-xs transition-colors ${isActive ? activeClass : inactiveClass}`
            }
          >
            <Users size={15} />
            <span>Users</span>
          </NavLink>
        </div>
      )}

      {/* ── Avion logo footer ── */}
      <div className="border-t border-border px-4 py-3 flex items-center justify-start">
        <AvionLogo width={130} variant={isDark ? 'dark' : 'light'} />
      </div>
    </aside>
  );
}
